import { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, Search, Plus, Tag as TagIcon, Pencil } from "lucide-react";

import { UnifiedModal } from "@/components/ui/unified-modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TagLabel } from "@/components/TagLabel";

import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

import { tagsService, TAG_COLORS, type Tag, type TagColor } from "@/services/tags.service";
import { handleInputFocus } from "@/utils/keyboard";
import { sortTags } from "@/lib/utils/tagSorting";

import { TagEditDialog } from "./TagEditDialog";
import { TagDeleteConfirm } from "./TagDeleteConfirm";

interface TagPickerStackedProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string;
  selectedTagIds?: string[];
  onTagsChange?: (tagIds: string[]) => void;
}

export function TagPickerStacked({
  open,
  onOpenChange,
  messageId,
  selectedTagIds = [],
  onTagsChange,
}: TagPickerStackedProps) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [orderedTags, setOrderedTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedTagIds));
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit/Delete states
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [deleteConfirmTag, setDeleteConfirmTag] = useState<{ tag: Tag; postCount: number } | null>(null);
  const [tagStats, setTagStats] = useState<Record<string, number>>({});

  // Ref for scroll area
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Filter tags based on search query and sort with selected tags first
  const filteredTags = useMemo(() => {
    // Base order is fixed on open to keep tag positions stable while selecting.
    const sorted = orderedTags.length > 0 ? orderedTags : sortTags(allTags, "recent");

    // Filter by search query if present
    let filtered = sorted;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = sorted.filter(tag => tag.name.toLowerCase().includes(query));
    }
    return filtered;
  }, [allTags, orderedTags, searchQuery]);

  // Check if search query would create a new tag
  const isNewTagName = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    return trimmed.length > 0 && trimmed.length <= 50 && !allTags.some(tag => tag.name.toLowerCase() === trimmed);
  }, [allTags, searchQuery]);

  // Load tags and current message tags
  useEffect(() => {
    if (!open || !user) return;

    loadData();
  }, [open, user, messageId]);

  // Initialize selected IDs only when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedTagIds));
      const selectedSet = new Set(selectedTagIds);
      const sorted = sortTags(allTags, "recent");
      const selectedFirst = sorted.filter(tag => selectedSet.has(tag.id));
      const unselected = sorted.filter(tag => !selectedSet.has(tag.id));
      setOrderedTags([...selectedFirst, ...unselected]);
    }
  }, [open]);

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Only fetch message tags if messageId is provided
      if (messageId) {
        const [tags, messageTags, stats] = await Promise.all([
          tagsService.listTags(),
          tagsService.getPostTags(messageId),
          tagsService.getTagStats(),
        ]);

        setAllTags(tags);
        const selectedSet = new Set(messageTags.map(tag => tag.id));
        const sorted = sortTags(tags, "recent");
        const selectedFirst = sorted.filter(tag => selectedSet.has(tag.id));
        const unselected = sorted.filter(tag => !selectedSet.has(tag.id));
        setOrderedTags([...selectedFirst, ...unselected]);
        setSelectedIds(new Set(messageTags.map(tag => tag.id)));
        setTagStats(stats);
      } else {
        // For new content (no messageId), just load tags and stats
        const [tags, stats] = await Promise.all([tagsService.listTags(), tagsService.getTagStats()]);

        setAllTags(tags);
        const selectedSet = new Set(selectedTagIds);
        const sorted = sortTags(tags, "recent");
        const selectedFirst = sorted.filter(tag => selectedSet.has(tag.id));
        const unselected = sorted.filter(tag => !selectedSet.has(tag.id));
        setOrderedTags([...selectedFirst, ...unselected]);
        // Keep the selectedTagIds that were passed in via props
        setSelectedIds(new Set(selectedTagIds));
        setTagStats(stats);
      }
    } catch (error: any) {
      console.error("Error loading tags:", error);
      toast({
        title: "Error loading tags",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!user || !searchQuery.trim() || creating) return;

    const name = searchQuery.trim();

    if (name.length > 50) {
      toast({
        title: "Tag name too long",
        description: "Tag names must be 50 characters or less",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    try {
      // Get a random color for new tag
      const randomColor = TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];

      const newTag = await tagsService.createTag({
        name,
        color: randomColor,
      });

      // Add to list, select it, and pin it to the top for this session.
      setAllTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedIds(prev => new Set([...prev, newTag.id]));
      setOrderedTags(prev => {
        const withoutNew = prev.filter(tag => tag.id !== newTag.id);
        return [newTag, ...withoutNew];
      });
      setSearchQuery("");

      toast({
        title: "Tag created",
        description: `Created tag "${newTag.name}"`,
      });
    } catch (error: any) {
      console.error("Error creating tag:", error);
      toast({
        title: "Error creating tag",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const toggleTag = (tagId: string) => {
    const wasSelected = selectedIds.has(tagId);

    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (saving) return;

    const tagIdsToSave = Array.from(selectedIds);

    // If no messageId, just return the selected tags without saving to backend
    // This handles the case of selecting tags for new content (before post is created)
    if (!messageId) {
      onTagsChange?.(tagIdsToSave);
      onOpenChange(false);
      return;
    }

    setSaving(true);
    try {
      // Set all selected tags on the post
      await tagsService.setPostTags(messageId, tagIdsToSave);

      // Notify parent component
      onTagsChange?.(tagIdsToSave);

      toast({
        title: "Tags saved",
        description: `Updated tags for this post`,
      });

      onOpenChange(false);
    } catch (error: any) {
      console.error("Error saving tags:", error);
      toast({
        title: "Error saving tags",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original selection
    setSelectedIds(new Set(selectedTagIds));
    setSearchQuery("");
    onOpenChange(false);
  };

  const handleEditTag = (tag: Tag) => {
    setEditingTag(tag);
  };

  const handleSaveEdit = async (tagId: string, name: string, color: TagColor) => {
    try {
      const updatedTag = await tagsService.updateTag(tagId, { name, color });

      // Update local state (no need to sort here - useMemo handles it)
      setAllTags(prev => prev.map(t => (t.id === tagId ? updatedTag : t)));

      toast({
        title: "Tag updated",
        description: `Updated "${name}"`,
      });
    } catch (error: any) {
      console.error("Error updating tag:", error);
      toast({
        title: "Error updating tag",
        description: error.message || "Please try again",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDeleteClick = (tag: Tag) => {
    const postCount = tagStats[tag.id] || 0;
    setDeleteConfirmTag({ tag, postCount });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmTag) return;

    const { tag } = deleteConfirmTag;

    try {
      await tagsService.deleteTag(tag.id);

      // Update local state
      setAllTags(prev => prev.filter(t => t.id !== tag.id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(tag.id);
        return next;
      });

      toast({
        title: "Tag deleted",
        description: `Deleted "${tag.name}"`,
      });
    } catch (error: any) {
      console.error("Error deleting tag:", error);
      toast({
        title: "Error deleting tag",
        description: error.message || "Please try again",
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <UnifiedModal
      open={open}
      onOpenChange={onOpenChange}
      icon={<TagIcon />}
      title="Manage Tags"
        description="Add or create tags for this post."
      stacked
      contentClassName="max-w-[90vw] sm:max-w-md"
    >
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 pt-2 pb-4">
          {/* Search/Create Input */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search or create tags..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
                autoFocus={false}
                onFocus={handleInputFocus}
                onKeyPress={e => {
                  if (e.key === "Enter" && isNewTagName) {
                    handleCreateTag();
                  }
                }}
              />
            </div>
            {isNewTagName && (
              <Button size="sm" onClick={handleCreateTag} disabled={creating} className="px-3">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                <span className="ml-1">Create</span>
              </Button>
            )}
          </div>

          {/* Tag List */}
          <div ref={scrollAreaRef}>
            <ScrollArea className="h-[280px] border rounded-xl p-3">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-brand-accent" />
                </div>
              ) : filteredTags.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {searchQuery && isNewTagName ? (
                    <p>Press Enter or click Create to add "{searchQuery}"</p>
                  ) : searchQuery ? (
                    <p>No tags found matching "{searchQuery}"</p>
                  ) : (
                    <p>No tags yet. Create your first tag above!</p>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredTags.map(tag => (
                    <div
                      key={tag.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg group cursor-pointer"
                      onClick={() => toggleTag(tag.id)}
                    >
                      <Checkbox
                        checked={selectedIds.has(tag.id)}
                        onCheckedChange={() => toggleTag(tag.id)}
                        onClick={e => e.stopPropagation()}
                      />
                      <div className="flex flex-1 items-center">
                        <TagLabel name={tag.name} color={tag.color} size="sm" truncate />
                      </div>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleEditTag(tag);
                        }}
                        className="p-1.5 rounded-md"
                        aria-label={`Edit ${tag.name}`}
                      >
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Actions */}
          <div className="flex flex-row gap-3">
            <Button variant="secondary" size="modal" onClick={handleCancel} className="flex-1">
              Cancel
            </Button>
            <Button size="modal" onClick={handleSave} disabled={saving} className="flex-1">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? "Saving..." : `Save Tags${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
            </Button>
          </div>
      </motion.div>

      {/* Edit Tag Dialog */}
      {editingTag && (
        <TagEditDialog
          open={!!editingTag}
          onOpenChange={open => !open && setEditingTag(null)}
          tag={editingTag}
          allTags={allTags}
          onSave={handleSaveEdit}
          onDelete={handleDeleteClick}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirmTag && (
        <TagDeleteConfirm
          open={!!deleteConfirmTag}
          onOpenChange={open => !open && setDeleteConfirmTag(null)}
          tagName={deleteConfirmTag.tag.name}
          postCount={deleteConfirmTag.postCount}
          onConfirm={handleConfirmDelete}
        />
      )}
    </UnifiedModal>
  );
}
